/**
 * adUnitTaxonomy.js
 *
 * Thin brand-free adapter over the App Pack store (src/api/appPacks.js).
 * Defines the expected GAM IU structure per app/publisher — APP_REGISTRY and
 * REDUNDANCY_OVERRIDES below are now DERIVED from each pack's `taxonomy` /
 * `taxonomy.redundancy` blocks instead of being hardcoded per brand. Onboard
 * a new app by dropping a pack JSON on the proxy — no code change here.
 *
 * All exported function/constant NAMES and SHAPES are unchanged so existing
 * callers (AdLifecycleView.jsx, InspectraNetworkView.jsx) need no changes.
 *
 * FNC IU formats (unchanged, still true for the seeded fnc pack):
 *   Standard:     4145/fnc/{platform}/{page}/{unit}
 *   Categorised:  4145/fnc/{platform}/{page}/{category}/{unit}
 *   Interstitial: 4145/fnc.int/{platform}
 */

import { getPacks, subscribeAppPacks } from '../api/appPacks';

// ─── App registry — derived from pack.taxonomy, rebuilt whenever the pack
// store updates (live fetch lands). Consumers only ever do
// `Object.values(APP_REGISTRY)` / read fields off each cfg — never rely on
// the object's keys — so keying by pack slug is safe.
// ────────────────────────────────────────────────────────────────────────
function _compileUrlToPage(rules = []) {
  // Pack schema stores `match` as a regex SOURCE string (JSON can't hold a
  // RegExp literal) — compile it here into the live RegExp shape the rest
  // of this file (pageFromUrl) has always expected.
  return rules.map(r => {
    let match = r.match;
    if (typeof match === 'string') {
      try { match = new RegExp(match); } catch { match = new RegExp(match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); }
    }
    return { match, page: r.page };
  });
}

function _buildAppRegistry(packs) {
  const registry = {};
  for (const pack of Object.values(packs || {})) {
    if (!pack || pack.enabled === false || !pack.taxonomy) continue;
    const t = pack.taxonomy;
    registry[pack.slug] = {
      label: pack.label || pack.slug,
      networkId: String(t.networkId ?? ''),
      publishers: t.publishers || [pack.slug],
      interstitialPublisher: t.interstitialPublisher || null,
      platforms: t.platforms || [],
      pagesFlat: t.pagesFlat || [],
      pagesCategorised: t.pagesCategorised || [],
      galleryPages: t.galleryPages || [],
      maxBannerIndex: t.maxBannerIndex ?? 25,
      specialUnits: t.specialUnits || [],
      urlToPage: _compileUrlToPage(t.urlToPage || []),
    };
  }
  return registry;
}

function _buildRedundancyOverrides(packs) {
  const overrides = {};
  for (const pack of Object.values(packs || {})) {
    if (!pack || pack.enabled === false || !pack.taxonomy?.redundancy) continue;
    const publishers = pack.taxonomy.publishers || [pack.slug];
    for (const pub of publishers) {
      overrides[pub] = { ...pack.taxonomy.redundancy };
    }
  }
  return overrides;
}

// Mutable live bindings — reassigned in place (not re-exported) so existing
// `import { APP_REGISTRY } from '...'` call sites keep working: ES module
// bindings are live references, so mutating the object contents (not
// reassigning the export binding itself) is visible to every importer.
export const APP_REGISTRY = _buildAppRegistry(getPacks());
export const REDUNDANCY_OVERRIDES = _buildRedundancyOverrides(getPacks());

function _refreshFromPacks(packs) {
  const fresh = _buildAppRegistry(packs);
  for (const k of Object.keys(APP_REGISTRY)) delete APP_REGISTRY[k];
  Object.assign(APP_REGISTRY, fresh);

  const freshOverrides = _buildRedundancyOverrides(packs);
  for (const k of Object.keys(REDUNDANCY_OVERRIDES)) delete REDUNDANCY_OVERRIDES[k];
  Object.assign(REDUNDANCY_OVERRIDES, freshOverrides);
}

subscribeAppPacks(_refreshFromPacks);

// ─── Redundancy / flood detection policy ─────────────────────
// Two tiers, by design:
//   GENERIC defaults — work on ANY GMA app with zero config, using only
//     GAM-native fields (content_url, iu, request timing/rate).
//   CUSTOMIZABLE overrides — per-app refinements (extra cust_params context
//     keys, unit roles) that sharpen detection but are never required.
//
// Guarantee: generic alone never produces a false fail and never misses a
// flood; an ambiguous case degrades to a warning. Overrides upgrade warnings
// to precise pass/fail.
export const REDUNDANCY_DEFAULTS = {
  contextKeys:   ['content_url'], // fields that identify a distinct page/context
  dupGapMs:      800,             // < this between same-context same-iu = duplicate
  minRefreshMs:  30_000,          // GAM minimum gap between legitimate refreshes
  scrollMinRun:  5,               // ≥ this many same-iu calls in a steady run = scroll feed
  scrollMinGapMs: 200,            // a real scroll feed paces ads ≥ this far apart
  floodTightMs:  120,             // sustained sub-this cadence = flood even for a feed unit
  anchorFloodMax: 3,              // > this many anchor-unit calls in one context/window = flood
  floodWindowMs: 30_000,          // window the flood ceilings apply over
};

/**
 * Classify an ad unit's role from its trailing segment — generic, name-based,
 * works for any app following the adh/mid/int + banN convention.
 *   'anchor' — one per page (adhesion/mid/interstitial); rapid repeats = bug
 *   'feed'   — in-feed banner (banN); many per page is expected (scroll)
 *   'unknown'— treat conservatively (like anchor) when unrecognised
 */
export function unitRole(iu = '') {
  const unit = (iu.split('/').filter(Boolean).pop() || '').toLowerCase();
  if (/^ban\d+$/.test(unit)) return 'feed';
  if (['adh', 'mid', 'int'].includes(unit)) return 'anchor';
  return 'unknown';
}

/**
 * True if the IU's page segment is a gallery/carousel page (same iu reused per
 * swipe). FNC: `slide`. IU format: /networkId/publisher/platform/page/[cat]/unit
 * so the page code is the 4th segment. Driven by APP_REGISTRY.galleryPages, so
 * adding a gallery page for any app is a one-line config change (in its pack).
 */
export function isGalleryPage(iu = '') {
  const parts = iu.split('/').filter(Boolean);
  const publisher = parts[1];
  const page      = parts[3];
  if (!page) return false;
  const detected = detectApp(iu);
  const galleryPages = detected?.cfg?.galleryPages
    || Object.values(APP_REGISTRY).find(cfg => cfg.publishers.includes(publisher))?.galleryPages
    || [];
  return galleryPages.includes(page);
}

/** Resolve the effective redundancy policy for an IU (defaults + overrides). */
export function redundancyPolicy(iu = '') {
  const publisher = iu.split('/').filter(Boolean)[1];
  const byPub = REDUNDANCY_OVERRIDES[publisher] || {};
  const detected = detectApp(iu);
  const byApp = detected ? (REDUNDANCY_OVERRIDES[detected.appKey] || {}) : {};
  return { ...REDUNDANCY_DEFAULTS, ...byPub, ...byApp };
}

// ─── Helpers ─────────────────────────────────────────────────

/** Detect which app config owns a given IU string */
export function detectApp(iu = '') {
  const parts = iu.split('/').filter(Boolean);
  const networkId = parts[0];
  const publisher  = parts[1];
  for (const [appKey, cfg] of Object.entries(APP_REGISTRY)) {
    if (cfg.networkId === networkId &&
        (cfg.publishers.includes(publisher) || publisher === cfg.interstitialPublisher)) {
      return { appKey, cfg };
    }
  }
  return null;
}

/** Derive expected page code from a page URL string */
export function pageFromUrl(pageUrl = '', cfg) {
  try {
    const pathname = new URL(pageUrl).pathname;
    for (const rule of cfg.urlToPage) {
      if (rule.match instanceof RegExp ? rule.match.test(pathname) : pathname.startsWith(rule.match)) {
        return rule.page;
      }
    }
  } catch {}
  return null;
}

/**
 * Validate a single IU string against the taxonomy.
 * Optionally pass `expectedPage` (derived from current page URL) to catch page mismatches.
 *
 * Returns: Array of { level: 'fail'|'warn'|'info', code, msg }
 */
export function validateIU(iu = '', { expectedPage = null, allowUnknownPage = false } = {}) {
  const issues = [];
  if (!iu) return [{ level: 'fail', code: 'iu_missing', msg: 'No ad unit (iu) in GAM request' }];

  const parts = iu.replace(/^\//, '').split('/').filter(Boolean);
  // Expected minimum parts: [networkId, publisher, platform, page, unit]

  const networkId  = parts[0];
  const publisher  = parts[1];
  const platform   = parts[2];

  // ── Detect app ──
  const detected = detectApp(iu);
  if (!detected) {
    return [{ level: 'warn', code: 'iu_unknown_app', msg: `IU does not match any known app taxonomy: ${iu}` }];
  }
  const { cfg } = detected;

  // ── Interstitial path: 4145/fnc.int/{platform} ──
  if (publisher === cfg.interstitialPublisher) {
    if (parts.length !== 3) {
      issues.push({ level: 'warn', code: 'int_format', msg: `Interstitial IU should be ${networkId}/${publisher}/{platform} (${parts.length} segments found)` });
    }
    if (platform && !cfg.platforms.includes(platform)) {
      issues.push({ level: 'fail', code: 'int_platform', msg: `Unknown platform in interstitial IU: ${platform}` });
    }
    return issues;
  }

  // ── Standard path ──
  if (parts.length < 5) {
    issues.push({ level: 'fail', code: 'iu_too_short', msg: `IU too short — expected at least 5 segments, got ${parts.length}: ${iu}` });
    return issues;
  }

  // Platform
  if (!cfg.platforms.includes(platform)) {
    issues.push({ level: 'fail', code: 'iu_platform', msg: `Unknown platform "${platform}" — expected one of: ${cfg.platforms.join(', ')}` });
  }

  const page = parts[3];
  const allPages = [...cfg.pagesFlat, ...cfg.pagesCategorised];

  // Page type — skip if caller has confirmed a custom override for this URL
  if (!allPages.includes(page) && !allowUnknownPage) {
    issues.push({ level: 'fail', code: 'iu_page', msg: `Unknown page type "${page}" — expected one of: ${allPages.join(', ')}` });
  }

  // Page match against current URL
  if (expectedPage && page !== expectedPage) {
    issues.push({
      level: 'fail',
      code: 'iu_page_mismatch',
      msg: `IU page mismatch — request is on "${expectedPage}" page but IU is for "${page}". Wrong slot firing.`,
    });
  }

  // Category segment
  const needsCategory = cfg.pagesCategorised.includes(page);
  if (needsCategory) {
    // Format: …/page/category/unit  → parts[4]=category, parts[5]=unit
    if (parts.length < 6) {
      issues.push({ level: 'warn', code: 'iu_missing_category', msg: `"${page}" page IUs require a category segment (e.g. /art/opin/ban1) — only ${parts.length} segments found` });
    }
  } else {
    // Format: …/page/unit  → parts[4]=unit
    if (parts.length > 5) {
      issues.push({ level: 'warn', code: 'iu_extra_segment', msg: `"${page}" page IUs should not have a category segment — got ${parts.length} segments` });
    }
  }

  // Unit (last segment)
  const unit = parts[parts.length - 1];

  if (cfg.specialUnits.includes(unit)) {
    // adh, etc. — always valid
  } else {
    const banMatch = unit.match(/^ban(\d+)$/i);
    if (!banMatch) {
      issues.push({ level: 'fail', code: 'iu_unit', msg: `Unknown ad unit "${unit}" — expected ban1–ban${cfg.maxBannerIndex} or ${cfg.specialUnits.join('/')}` });
    } else {
      const idx = parseInt(banMatch[1], 10);
      if (idx < 1) {
        issues.push({ level: 'fail', code: 'iu_ban_index', msg: `Invalid banner index: ban${idx}` });
      } else if (idx > cfg.maxBannerIndex) {
        issues.push({
          level: 'warn',
          code: 'iu_ban_overflow',
          msg: `Banner index ban${idx} exceeds max (ban${cfg.maxBannerIndex}) — should repeat ban${cfg.maxBannerIndex}`,
        });
      }
    }
  }

  return issues;
}
